import * as React from "react"
import { useTracker, GAEventType } from "@modules/analytics"
import { GatsbyCloudLogo } from "@modules/brand/assets/GatsbyCloudLogo"
import { GatsbyCloudWordmark } from "@modules/brand/assets/GatsbyCloudWordmark"
import UserMenu from "./UserMenu"
import { StandardSingleColumn } from "@modules/ui/layouts/Containers"
import { Heading, Spacer, ThemeCss } from "gatsby-interface"
import VisuallyHidden from "@modules/a11y/components/VisuallyHidden"
import { UserOrganizationsHeader } from "./UserOrganizationsHeader"
import { HelpMenu } from "./HelpMenu"
import { Link } from "gatsby"
import UpgradeLink from "./UpgradeLink"
import { NewsLoaded } from "@modules/user/news/NewsLoaded"
import { MarketingBanner } from "@modules/banner"

export type HeaderProps = {
  pathname: string
}

export function Header({ pathname }: HeaderProps) {
  const { trackGoogleAnalytics } = useTracker()
  const welcomeFormScreen = pathname.includes(`dashboard/welcome`)
  const [orgIsBlocked, setOrgIsBlocked] = React.useState<
    boolean | null | undefined
  >(false)
  const [orgWillBeBlockedAt, setOrgWillBeBlockedAt] = React.useState<
    string | null | undefined
  >()
  const [orgCanBeUnblocked, setOrgCanBeUnblocked] = React.useState<
    boolean | null | undefined
  >()

  const hiddenOrganizationHeadersOnPaths = [
    `dashboard/welcome`,
    `dashboard/sites/provision`,
    `dashboard/sites/create`,
    `sites/provision`,
    `sites/create`,
    `sites/import`,
    `release-notes`,
  ]

  const hideOrganizationHeader = hiddenOrganizationHeadersOnPaths.some(item =>
    pathname.includes(item)
  )

  // Grab organization id from path.
  let organizationId = ""
  if (pathname.includes("organization")) {
    organizationId = pathname.split("/")[3]
  } else {
    organizationId = pathname.split("/")[2]
  }

  return (
    <React.Fragment>
      {orgIsBlocked === false && organizationId && (
        <MarketingBanner
          orgId={organizationId}
          orgWillBeBlockedAt={orgWillBeBlockedAt}
          orgCanBeUnblocked={orgCanBeUnblocked}
        />
      )}

      <header css={rootCss}>
        <StandardSingleColumn>
          <nav aria-label="Header navigation" css={navCss}>
            <div css={mainWrapperCss}>
              <div css={leftWrapperCss}>
                {!welcomeFormScreen ? (
                  <Link
                    to="/dashboard/sites"
                    data-cy="header-logo-link"
                    onClick={() =>
                      trackGoogleAnalytics({
                        eventType: GAEventType.Click,
                        category: `Product Engagement - Dashboard`,
                        label: { loc: `Header`, text: `logo` },
                      })
                    }
                    css={logoContainerCss}
                  >
                    <Logo />
                  </Link>
                ) : (
                  <div css={logoContainerCss}>
                    <Logo />
                  </div>
                )}
                <span css={userOrgRowStyle}>
                  {!hideOrganizationHeader && (
                    <UserOrganizationsHeader
                      pathname={pathname}
                      cleanHeader={setOrgIsBlocked}
                      announceBlockAt={setOrgWillBeBlockedAt}
                      enableUnblocking={setOrgCanBeUnblocked}
                    />
                  )}
                </span>
              </div>

              <div css={userCss}>
                {!orgIsBlocked && (
                  <>
                    <NewsLoaded />
                    <Spacer direction="horizontal" size={7} />
                    <UpgradeLink />
                    <Spacer direction="horizontal" size={5} />
                    <HelpMenu organizationId={organizationId} />
                  </>
                )}
                <Spacer direction="horizontal" size={3} />
                <UserMenu />
              </div>
            </div>
            <span css={userOrgColumnStyle}>
              {!hideOrganizationHeader && (
                <UserOrganizationsHeader
                  pathname={pathname}
                  cleanHeader={setOrgIsBlocked}
                  announceBlockAt={setOrgWillBeBlockedAt}
                  enableUnblocking={setOrgCanBeUnblocked}
                />
              )}
            </span>
          </nav>
        </StandardSingleColumn>
      </header>
    </React.Fragment>
  )
}

/* elements */

function Logo() {
  return (
    <React.Fragment>
      <GatsbyCloudLogo css={logoCss} />
      <Heading css={titleCss} as="span">
        <VisuallyHidden>Gatsby</VisuallyHidden>
        {` `}
        <GatsbyCloudWordmark css={logoCss} />
      </Heading>
    </React.Fragment>
  )
}

/* styles */

const rootCss: ThemeCss = theme => ({
  background: theme.colors.primaryBackground,
  width: `100%`,
  zIndex: 1000,

  ".dashboardGreyBg &": {
    background: theme.colors.secondaryBackground,
  },
})

const mainWrapperCss: ThemeCss = theme => ({
  display: `flex`,
  width: `100%`,
  justifyContent: `space-between`,
  flexWrap: `wrap`,
  rowGap: theme.space[4],
  columnGap: theme.space[7],
  // Prevent layout shift/jumping; tallest element currently is 40 pixels high
  minHeight: "40px",

  [theme.mediaQueries.tablet]: {
    flexWrap: `nowrap`,
  },
})

const leftWrapperCss: ThemeCss = theme => ({
  display: `flex`,
  flexDirection: `column`,
  justifyContent: `center`,
  columnGap: theme.space[7],
  rowGap: theme.space[4],
  overflow: `hidden`,
  [theme.mediaQueries.tablet]: {
    flexDirection: `row`,
    justifyContent: `flex-start`,
  },
})

const userOrgRowStyle: ThemeCss = theme => ({
  display: `none`,
  [theme.mediaQueries.tablet]: {
    display: `flex`,
    alignItems: `center`,
    overflow: `hidden`,
  },
})

const userOrgColumnStyle: ThemeCss = theme => ({
  display: `block`,
  overflow: `hidden`,
  [theme.mediaQueries.tablet]: {
    display: `none`,
  },
})

const navCss: ThemeCss = theme => ({
  alignItems: `center`,
  display: `flex`,
  margin: `${theme.space[5]} 0 ${theme.space[4]}`,
  flexWrap: `wrap`,
  justifyContent: `space-between`,
  columnGap: theme.space[7],
  rowGap: theme.space[4],
  [theme.mediaQueries.tablet]: {
    flexWrap: `nowrap`,
  },
})

const titleCss: ThemeCss = theme => ({
  alignItems: "center",
  color: theme.colors.black,
  display: "flex",
  fontSize: theme.fontSizes[4],
  fontWeight: theme.fontWeights.bold,
  textRendering: `optimizeLegibility`,
})

const logoContainerCss: ThemeCss = theme => ({
  display: "flex",
  alignItems: "center",
  color: theme.colors.black,
  flexGrow: 0,
  textDecoration: "none",
  gap: theme.space[3],

  "&:hover svg, &:focus svg": {
    color: theme.colors.gatsby,
  },
})

const logoCss: ThemeCss = () => ({
  width: `auto`,
  height: `24px`,
})

const userCss: ThemeCss = () => ({
  display: "flex",
  flexShrink: 0,
  justifyContent: `flex-end`,
  marginLeft: 0,
})
